import { useCallback, useRef } from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED } from '../consts';
import TEXTS from '../texts';
import type { IncomeProtectionDaSettingType, SettingsFormInitialValuesType } from '../types';

export const CUSTOM_PAYMENT_FREQUENCY_VALUE = 'custom';
export const PAYMENT_FREQUENCY_OPTIONS = [
  { value: 7, label: TEXTS.week },
  { value: 14, label: TEXTS.fortnight },
  { value: CUSTOM_PAYMENT_FREQUENCY_VALUE, label: TEXTS.custom },
] as const;
const INITIAL_PAYMENT_FREQUENCY_VALUE = PAYMENT_FREQUENCY_OPTIONS[1].value;

export const canTreatInitialDaysValueAsWeeks = (value?: string | number | null): boolean => {
  return !value || isNaN(Number(value)) || Number(value) % 7 === 0;
};

const getInitialBenefitPeriodInternal = (benefitPeriodNumOfDays?: number): string | number => {
  return canTreatInitialDaysValueAsWeeks(benefitPeriodNumOfDays) && !isNaN(Number(benefitPeriodNumOfDays))
    ? Number(benefitPeriodNumOfDays) / 7
    : (benefitPeriodNumOfDays ?? '');
};

export const getInitialValues = (
  exposureId: number,
  setting?: IncomeProtectionDaSettingType
): SettingsFormInitialValuesType => ({
  exposure_id: exposureId,
  payee_contact_id: setting?.payee_contact_id ?? null,
  waiting_period_num_of_days: setting?.waiting_period_num_of_days ?? '',
  benefit_period_num_of_days: setting?.benefit_period_num_of_days ?? null,
  start_date: setting?.start_date ?? null,
  payment_frequency_num_of_days: setting?.payment_frequency_num_of_days ?? INITIAL_PAYMENT_FREQUENCY_VALUE,
  average_weekly_income: setting?.average_weekly_income ?? '',
  weekly_payment_limit: setting?.weekly_payment_limit ?? '',
  weekly_income_percentage: setting?.weekly_income_percentage ?? '',
  claimant_weekly_limit: setting?.claimant_weekly_limit ?? '',
  // used internally to manage the payment_frequency_num_of_days value
  payment_frequency_internal: setting
    ? PAYMENT_FREQUENCY_OPTIONS.find((option) => option.value === setting.payment_frequency_num_of_days)?.value ||
      CUSTOM_PAYMENT_FREQUENCY_VALUE
    : INITIAL_PAYMENT_FREQUENCY_VALUE,
  // values managed and set by ContactTextFieldFormik (along with payee_contact_id):
  payee_contact: null,
  payee_contact_full_name: setting?.payee_contact_full_name ?? '',
  benefit_period_internal: getInitialBenefitPeriodInternal(setting?.benefit_period_num_of_days),
});

export const validationSchema = Yup.object().shape({
  payee_contact_id: Yup.number().nullable().required(TEXTS.required),
  benefit_period_internal: Yup.number()
    .nullable()
    .integer(TEXTS.mustBeAnInteger)
    .min(0, TEXTS.cannotBeNegative)
    .required(TEXTS.required),
  waiting_period_num_of_days: Yup.number()
    .nullable()
    .integer(TEXTS.mustBeAnInteger)
    .min(1, TEXTS.mustBeGreaterThan0)
    .required(TEXTS.required),
  start_date: Yup.date().nullable().required(TEXTS.required),
  payment_frequency_num_of_days: Yup.number()
    .nullable()
    .integer(TEXTS.mustBeAnInteger)
    .min(1, TEXTS.mustBeGreaterThan0)
    .required(TEXTS.required),
  average_weekly_income: Yup.number().nullable().min(0, TEXTS.cannotBeNegative).required(TEXTS.required),
  weekly_payment_limit: Yup.number().nullable().min(0, TEXTS.cannotBeNegative).required(TEXTS.required),
  weekly_income_percentage: Yup.number()
    .nullable()
    .min(0, TEXTS.mustBeBetween0And100)
    .max(100, TEXTS.mustBeBetween0And100)
    .required(TEXTS.required),
  claimant_weekly_limit: Yup.number().nullable().min(0, TEXTS.cannotBeNegative).required(TEXTS.required),
});

export const useHandleChangeAndComputeClaimantWeeklyLimit = () => {
  const { setFieldValue, values } = useFormikContext<SettingsFormInitialValuesType>();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const computeClaimantWeeklyLimit = useCallback(() => {
    const averageWeeklyIncomeValue = Number(valuesRef.current.average_weekly_income);
    const weeklyIncomePercentageValue = Number(valuesRef.current.weekly_income_percentage);
    const weeklyPaymentLimitValue = Number(valuesRef.current.weekly_payment_limit);
    if (isNaN(averageWeeklyIncomeValue) || isNaN(weeklyIncomePercentageValue) || isNaN(weeklyPaymentLimitValue)) {
      return;
    }
    const claimantWeeklyLimitValue = Math.min(
      (averageWeeklyIncomeValue * weeklyIncomePercentageValue) / 100,
      weeklyPaymentLimitValue
    ).toFixed(2);
    setFieldValue(
      INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.claimant_weekly_limit,
      Number(claimantWeeklyLimitValue)
    );
  }, [setFieldValue]);

  return useCallback(
    async (
      key: 'average_weekly_income' | 'weekly_income_percentage' | 'weekly_payment_limit',
      value: string | number | null
    ) => {
      await setFieldValue(key, value); // setFieldValue returns a Promise, though its type does not reflect this
      computeClaimantWeeklyLimit();
    },
    [setFieldValue, computeClaimantWeeklyLimit]
  );
};
